// ABOUTME: Component for displaying business recommendations
// ABOUTME: Shows immediate actions, strategic initiatives, and monitoring metrics

import React from 'react';
import { BusinessRecommendations, RecommendationItem } from '@/types/analysis';

interface RecommendationsProps {
  recommendations: BusinessRecommendations;
}

export const Recommendations: React.FC<RecommendationsProps> = ({ recommendations }) => {
  const renderRecommendationItem = (item: string | RecommendationItem | any) => {
    if (typeof item === 'string') {
      return item;
    }

    // Check if it's an empty object or has no relevant content
    if (!item || (typeof item === 'object' && !item.action && !item.description && !item.consideration && !item.recommendation)) {
      return null;
    }

    return (
      <div className="space-y-2">
        <div className="font-medium">{item.action || item.recommendation || item.consideration || ''}</div>
        {(item.description || item.rationale) && (
          <div className="text-sm text-gray-600">{item.description || item.rationale}</div>
        )}
        {item.implementation && (
          <div className="text-sm text-gray-500">Implementation: {item.implementation}</div>
        )}
        <div className="flex flex-wrap gap-2 text-xs">
          {item.priority && (
            <span className="px-2 py-1 bg-gray-100 rounded">Priority: {item.priority}</span>
          )}
          {item.timeline && (
            <span className="px-2 py-1 bg-gray-100 rounded">Timeline: {item.timeline}</span>
          )}
          {item.estimated_impact && (
            <span className="px-2 py-1 bg-gray-100 rounded">Impact: {item.estimated_impact}</span>
          )}
          {item.expected_impact && (
            <span className="px-2 py-1 bg-gray-100 rounded">Expected: {item.expected_impact}</span>
          )}
          {item.implementation_complexity && (
            <span className="px-2 py-1 bg-gray-100 rounded">Complexity: {item.implementation_complexity}</span>
          )}
        </div>
        {item.success_metric && (
          <div className="text-xs text-gray-500 italic">Success Metric: {item.success_metric}</div>
        )}
      </div>
    );
  };

  return (
    <div className="mb-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Business Recommendations</h3>

      {recommendations.immediate_actions && recommendations.immediate_actions.length > 0 && (
        <div className="bg-red-50 border-l-4 border-red-400 rounded-lg p-4 mb-4">
          <h4 className="font-semibold text-red-800 mb-3">🚨 Immediate Actions</h4>
          <ul className="space-y-3">
            {recommendations.immediate_actions.map((action, idx) => {
              const content = renderRecommendationItem(action);
              if (!content) return null;
              return (
                <li key={idx} className="flex items-start text-gray-700">
                  <span className="mr-2 text-red-500 mt-1">•</span>
                  <div className="flex-1">
                    {content}
                  </div>
                </li>
              );
            }).filter(Boolean)}
          </ul>
        </div>
      )}

      {recommendations.strategic_initiatives && recommendations.strategic_initiatives.length > 0 && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-lg p-4 mb-4">
          <h4 className="font-semibold text-yellow-800 mb-3">🎯 Strategic Initiatives</h4>
          <ul className="space-y-3">
            {recommendations.strategic_initiatives.map((initiative: any, idx) => {
              // Handle string format for strategic_initiatives
              if (typeof initiative === 'string') {
                return (
                  <li key={idx} className="flex items-start text-gray-700">
                    <span className="mr-2 text-yellow-600">•</span>
                    <span>{initiative}</span>
                  </li>
                );
              }
              return (
                <li key={idx} className="flex items-start text-gray-700">
                  <span className="mr-2 text-yellow-600 mt-1">•</span>
                  <div className="flex-1">
                    {renderRecommendationItem(initiative)}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {recommendations.strategic_considerations && recommendations.strategic_considerations.length > 0 && (
        <div className="bg-blue-50 border-l-4 border-blue-400 rounded-lg p-4 mb-4">
          <h4 className="font-semibold text-blue-800 mb-3">🔮 Strategic Considerations</h4>
          <ul className="space-y-3">
            {recommendations.strategic_considerations.map((consideration: any, idx) => {
              // Handle both string and object formats
              if (typeof consideration === 'string') {
                return (
                  <li key={idx} className="flex items-start text-gray-700">
                    <span className="mr-2 text-blue-600">•</span>
                    <span>{consideration}</span>
                  </li>
                );
              }
              return (
                <li key={idx} className="text-gray-700">
                  {consideration.consideration && (
                    <div className="font-medium">{consideration.consideration}</div>
                  )}
                  {consideration.description && (
                    <div className="text-sm text-gray-600 mt-1">{consideration.description}</div>
                  )}
                  {(consideration.timeline || consideration.rationale) && (
                    <div className="text-xs text-gray-500 mt-1">
                      {consideration.timeline && `Timeline: ${consideration.timeline}`}
                      {consideration.timeline && consideration.rationale && ' | '}
                      {consideration.rationale && `Rationale: ${consideration.rationale}`}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {recommendations.risk_factors && recommendations.risk_factors.length > 0 && (
        <div className="bg-orange-50 border-l-4 border-orange-400 rounded-lg p-4 mb-4">
          <h4 className="font-semibold text-orange-800 mb-3">⚠️ Risk Factors</h4>
          <ul className="space-y-3">
            {recommendations.risk_factors.map((risk: any, idx) => {
              // Handle string format
              if (typeof risk === 'string') {
                return (
                  <li key={idx} className="flex items-start text-gray-700">
                    <span className="mr-2 text-orange-500">•</span>
                    <span>{risk}</span>
                  </li>
                );
              }
              // Handle object format
              return (
                <li key={idx} className="text-gray-700">
                  {risk.risk && <div className="font-medium">{risk.risk}</div>}
                  {risk.description && (
                    <div className="text-sm text-gray-600 mt-1">{risk.description}</div>
                  )}
                  {(risk.severity || risk.mitigation) && (
                    <div className="text-xs text-gray-500 mt-1">
                      {risk.severity && `Severity: ${risk.severity}`}
                      {risk.severity && risk.mitigation && ' | '}
                      {risk.mitigation && `Mitigation: ${risk.mitigation}`}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {recommendations.success_amplification && recommendations.success_amplification.length > 0 && (
        <div className="bg-green-50 border-l-4 border-green-400 rounded-lg p-4 mb-4">
          <h4 className="font-semibold text-green-800 mb-3">🚀 Success Amplification</h4>
          <ul className="space-y-3">
            {recommendations.success_amplification.map((success: any, idx) => {
              // Handle string format
              if (typeof success === 'string') {
                return (
                  <li key={idx} className="flex items-start text-gray-700">
                    <span className="mr-2 text-green-600">•</span>
                    <span>{success}</span>
                  </li>
                );
              }
              // Handle object format
              return (
                <li key={idx} className="text-gray-700">
                  {success.pattern && <div className="font-medium">{success.pattern}</div>}
                  {success.opportunity && (
                    <div className="text-sm text-gray-600 mt-1">{success.opportunity}</div>
                  )}
                  {success.amplification_strategy && (
                    <div className="text-sm text-gray-600 mt-1">
                      <span className="font-medium">Strategy:</span> {success.amplification_strategy}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {recommendations.monitoring_metrics && recommendations.monitoring_metrics.length > 0 && (
        <div className="bg-green-50 border-l-4 border-green-400 rounded-lg p-4">
          <h4 className="font-semibold text-green-800 mb-3">📊 Monitoring Metrics</h4>
          <ul className="space-y-2">
            {recommendations.monitoring_metrics.map((metric, idx) => (
              <li key={idx} className="flex items-start text-gray-700">
                <span className="mr-2 text-green-600">•</span>
                <span>{typeof metric === 'object' ? (metric as any).action || (metric as any).description || JSON.stringify(metric) : metric}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
